import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { DatePickerFr } from './ui/DatePickerFr';
import { Search, X, Calendar, Users, FileText, ChevronDown, ChevronUp, RotateCcw, SlidersHorizontal } from 'lucide-react';

export default function ConventionFilters({ 
  conventions = [],
  onFilter,
  coopersList: coopersList = [],
  expertsList: expertsList = []
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    type: 'all',
    statut: 'all',
    destinataire: 'all',
    dateDebut: '',
    dateFin: '',
    dureeMin: '',
    dureeMax: ''
  });
  
  // Initialiser les conventions filtrées au chargement
  useEffect(() => {
    if (conventions.length > 0) {
      onFilter(conventions);
    }
  }, [conventions]);
  
  // Compteurs par statut
  const statusCounts = useMemo(() => {
    const counts = {
      tous: conventions.length,
      brouillon: 0,
      en_attente_signature: 0,
      actif: 0,
      suspendu: 0,
      resilie: 0,
      archive: 0
    };
    conventions.forEach(c => {
      if (counts[c.statut] !== undefined) counts[c.statut]++;
    });
    return counts;
  }, [conventions]);
  
  // Compteurs par type
  const typeCounts = useMemo(() => {
    const counts = { cooper: 0, expert: 0, autre: 0 };
    conventions.forEach(c => {
      const type = c.type_contrat || c.type_convention;
      if (counts[type] !== undefined) counts[type]++;
    });
    return counts;
  }, [conventions]);
  
  // Appliquer les filtres
  const applyFilters = (newFilters) => {
    setFilters(newFilters);
    
    let filtered = [...conventions];
    
    // Recherche textuelle
    if (newFilters.search) {
      const searchLower = newFilters.search.toLowerCase();
      filtered = filtered.filter(c => 
        (c.titre || '').toLowerCase().includes(searchLower) ||
        (c.numero_contrat || '').toLowerCase().includes(searchLower) ||
        (c.destinataire_nom || '').toLowerCase().includes(searchLower) ||
        (c.destinataire_email || '').toLowerCase().includes(searchLower)
      );
    }
    
    // Filtre par type
    if (newFilters.type !== 'all') {
      filtered = filtered.filter(c => 
        (c.type_contrat || c.type_convention) === newFilters.type
      );
    }
    
    // Filtre par statut
    if (newFilters.statut !== 'all') {
      filtered = filtered.filter(c => c.statut === newFilters.statut);
    }
    
    // Filtre par destinataire
    if (newFilters.destinataire !== 'all') {
      filtered = filtered.filter(c => c.destinataire_id === newFilters.destinataire);
    }
    
    // Filtre par date de création (début)
    if (newFilters.dateDebut) {
      const dateDebut = new Date(newFilters.dateDebut);
      filtered = filtered.filter(c => new Date(c.created_at) >= dateDebut);
    }
    
    // Filtre par date de création (fin)
    if (newFilters.dateFin) {
      const dateFin = new Date(newFilters.dateFin);
      dateFin.setHours(23, 59, 59);
      filtered = filtered.filter(c => new Date(c.created_at) <= dateFin);
    }
    
    // Filtre par durée min
    if (newFilters.dureeMin) {
      filtered = filtered.filter(c => (c.duree_mois || 0) >= parseInt(newFilters.dureeMin));
    }
    
    // Filtre par durée max
    if (newFilters.dureeMax) {
      filtered = filtered.filter(c => (c.duree_mois || 0) <= parseInt(newFilters.dureeMax));
    }
    
    onFilter(filtered);
  };
  
  // Mettre à jour un filtre
  const updateFilter = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    applyFilters(newFilters);
  };
  
  // Réinitialiser les filtres
  const resetFilters = () => {
    const defaultFilters = {
      search: '',
      type: 'all',
      statut: 'all',
      destinataire: 'all',
      dateDebut: '',
      dateFin: '',
      dureeMin: '',
      dureeMax: ''
    };
    applyFilters(defaultFilters);
  };
  
  // Nombre de filtres actifs
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.search) count++;
    if (filters.type !== 'all') count++;
    if (filters.statut !== 'all') count++;
    if (filters.destinataire !== 'all') count++;
    if (filters.dateDebut) count++;
    if (filters.dateFin) count++;
    if (filters.dureeMin) count++;
    if (filters.dureeMax) count++;
    return count;
  }, [filters]);
  
  // Liste des destinataires uniques
  const destinataires = useMemo(() => {
    const unique = new Map();
    conventions.forEach(c => {
      if (c.destinataire_id && c.destinataire_nom) {
        unique.set(c.destinataire_id, c.destinataire_nom);
      }
    });
    return Array.from(unique, ([id, nom]) => ({ id, nom }));
  }, [conventions]);

  return (
    <Card>
      <CardContent className="pt-4">
        {/* Barre de recherche principale */}
        <div className="flex flex-wrap gap-3 items-center">
          {/* Recherche */}
          <div className="relative flex-1 min-w-[250px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Rechercher par titre, numéro, destinataire..."
              value={filters.search}
              onChange={(e) => updateFilter('search', e.target.value)}
              className="pl-10"
            />
            {filters.search && (
              <Button
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
                onClick={() => updateFilter('search', '')}
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>
          
          {/* Filtre Type */}
          <Select value={filters.type} onValueChange={(v) => updateFilter('type', v)}>
            <SelectTrigger className="w-[180px]">
              <FileText className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les types</SelectItem>
              <SelectItem value="cooper">
                <span className="flex items-center gap-2">
                  🔵 COOPER&apos;S ({typeCounts.cooper})
                </span>
              </SelectItem>
              <SelectItem value="expert">
                <span className="flex items-center gap-2">
                  🟢 EXPERT ({typeCounts.expert})
                </span>
              </SelectItem>
              <SelectItem value="autre">
                <span className="flex items-center gap-2">
                  🟣 Autre ({typeCounts.autre})
                </span>
              </SelectItem>
            </SelectContent>
          </Select>
          
          {/* Filtre Statut */}
          <Select value={filters.statut} onValueChange={(v) => updateFilter('statut', v)}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts ({statusCounts.tous})</SelectItem>
              <SelectItem value="brouillon">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-slate-400"></span>
                  Brouillon ({statusCounts.brouillon})
                </span>
              </SelectItem>
              <SelectItem value="en_attente_signature">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                  En attente ({statusCounts.en_attente_signature})
                </span>
              </SelectItem>
              <SelectItem value="actif">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-green-500"></span>
                  Actif ({statusCounts.actif})
                </span>
              </SelectItem>
              <SelectItem value="suspendu">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                  Suspendu ({statusCounts.suspendu})
                </span>
              </SelectItem>
              <SelectItem value="resilie">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-red-500"></span>
                  Résilié ({statusCounts.resilie})
                </span>
              </SelectItem>
              <SelectItem value="archive">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-gray-500"></span>
                  Archivé ({statusCounts.archive})
                </span>
              </SelectItem>
            </SelectContent>
          </Select>
          
          {/* Bouton Filtres avancés */}
          <Button
            variant="outline"
            onClick={() => setIsExpanded(!isExpanded)}
            className={activeFiltersCount > 2 ? 'border-blue-500 text-blue-600' : ''}
          >
            <SlidersHorizontal className="h-4 w-4 mr-2" />
            Filtres avancés
            {activeFiltersCount > 0 && (
              <Badge className="ml-2 bg-blue-600 text-white font-semibold">{activeFiltersCount}</Badge>
            )}
            {isExpanded ? <ChevronUp className="h-4 w-4 ml-2" /> : <ChevronDown className="h-4 w-4 ml-2" />}
          </Button>
          
          {/* Bouton Reset */}
          {activeFiltersCount > 0 && (
            <Button variant="ghost" size="sm" onClick={resetFilters}>
              <RotateCcw className="h-4 w-4 mr-1" />
              Réinitialiser
            </Button>
          )}
        </div>
        
        {/* Filtres avancés (expandable) */}
        {isExpanded && (
          <div className="mt-4 pt-4 border-t grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Filtre Destinataire */}
            <div>
              <Label className="text-sm font-medium mb-2 block">
                <Users className="h-4 w-4 inline mr-1" />
                Destinataire
              </Label>
              <Select value={filters.destinataire} onValueChange={(v) => updateFilter('destinataire', v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Tous les destinataires" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les destinataires</SelectItem>
                  {destinataires.map(d => (
                    <SelectItem key={d.id} value={d.id}>{d.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Date de création - Début */}
            <div>
              <Label className="text-sm font-medium mb-2 block">
                <Calendar className="h-4 w-4 inline mr-1" />
                Date de création (depuis)
              </Label>
              <DatePickerFr
                className="w-full"
                value={filters.dateDebut}
                onChange={(v) => updateFilter('dateDebut', v)}
              />
            </div>
            
            {/* Date de création - Fin */}
            <div>
              <Label className="text-sm font-medium mb-2 block">
                <Calendar className="h-4 w-4 inline mr-1" />
                Date de création (jusqu&apos;à)
              </Label>
              <DatePickerFr
                className="w-full"
                value={filters.dateFin}
                onChange={(v) => updateFilter('dateFin', v)}
              />
            </div>
            
            {/* Durée */}
            <div>
              <Label className="text-sm font-medium mb-2 block">
                Durée (mois)
              </Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  placeholder="Min"
                  value={filters.dureeMin}
                  onChange={(e) => updateFilter('dureeMin', e.target.value)}
                  className="w-20"
                  min="0"
                />
                <span className="self-center">-</span>
                <Input
                  type="number"
                  placeholder="Max"
                  value={filters.dureeMax}
                  onChange={(e) => updateFilter('dureeMax', e.target.value)}
                  className="w-20"
                  min="0"
                />
              </div>
            </div>
          </div>
        )}
        
        {/* Tags des filtres actifs */}
        {activeFiltersCount > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {filters.search && (
              <Badge variant="secondary" className="flex items-center gap-1">
                Recherche: &quot;{filters.search}&quot;
                <X className="h-3 w-3 cursor-pointer" onClick={() => updateFilter('search', '')} />
              </Badge>
            )}
            {filters.type !== 'all' && (
              <Badge variant="secondary" className="flex items-center gap-1">
                Type: {filters.type.toUpperCase()}
                <X className="h-3 w-3 cursor-pointer" onClick={() => updateFilter('type', 'all')} />
              </Badge>
            )}
            {filters.statut !== 'all' && (
              <Badge variant="secondary" className="flex items-center gap-1">
                Statut: {filters.statut.replace('_', ' ')}
                <X className="h-3 w-3 cursor-pointer" onClick={() => updateFilter('statut', 'all')} />
              </Badge>
            )}
            {filters.destinataire !== 'all' && (
              <Badge variant="secondary" className="flex items-center gap-1">
                Destinataire: {destinataires.find(d => d.id === filters.destinataire)?.nom || filters.destinataire}
                <X className="h-3 w-3 cursor-pointer" onClick={() => updateFilter('destinataire', 'all')} />
              </Badge>
            )}
            {filters.dateDebut && (
              <Badge variant="secondary" className="flex items-center gap-1">
                Depuis: {filters.dateDebut}
                <X className="h-3 w-3 cursor-pointer" onClick={() => updateFilter('dateDebut', '')} />
              </Badge>
            )}
            {filters.dateFin && (
              <Badge variant="secondary" className="flex items-center gap-1">
                Jusqu&apos;à: {filters.dateFin}
                <X className="h-3 w-3 cursor-pointer" onClick={() => updateFilter('dateFin', '')} />
              </Badge>
            )}
            {filters.dureeMin && (
              <Badge variant="secondary" className="flex items-center gap-1">
                Durée min: {filters.dureeMin} mois
                <X className="h-3 w-3 cursor-pointer" onClick={() => updateFilter('dureeMin', '')} />
              </Badge>
            )}
            {filters.dureeMax && (
              <Badge variant="secondary" className="flex items-center gap-1">
                Durée max: {filters.dureeMax} mois
                <X className="h-3 w-3 cursor-pointer" onClick={() => updateFilter('dureeMax', '')} />
              </Badge>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
